import { useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent as ReactDragEvent, MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { ColumnChooser } from './ColumnChooser';
import type { HeaderMenuColumns } from './useHeaderMenu';
import { loadGridPrefs, saveGridPrefs } from './gridPrefs';

/**
 * Column model for ANY array-driven grid — Hide / Show-all / Column Chooser /
 * drag-reorder / resize, all in-memory per grid instance. The shared
 * <AppDataGrid> has this built in; hand-rolled tables (ModuleListView, reporting
 * lists, …) adopt it with this hook so every grid behaves identically (client
 * 2026-08-03 "all the grids, list AND detail — global requirement").
 *
 * Usage (a table that renders from a `cols` array of `{ key, label, width }`):
 *
 *   const cm = useColumnManager(cols, (c) => c.key, (c) => c.label);
 *   const { openHeaderMenu, headerMenu } = useHeaderMenu(sorter, cm.columnMenu);
 *   …
 *   {cm.visibleColumns.map((c) => (
 *     <th key={c.key}
 *         className={cm.dragClass(c.key)}
 *         style={{ width: cm.colWidth(c.key) ?? c.width }}
 *         onContextMenu={(e) => openHeaderMenu(e, { field: c.key, label: c.label })}
 *         {...cm.dragProps(c.key)}>
 *       … header … <span className="adg-resize" onMouseDown={(e) => cm.startResize(c.key, e)} />
 *     </th>
 *   ))}
 *   {cm.visibleColumns.map((c) => <td key={c.key}>…</td>)}   // body in the SAME order
 *   {headerMenu}
 *   {cm.chooser}
 */
export function useColumnManager<C>(
  columns: C[],
  keyOf: (c: C) => string,
  labelOf: (c: C) => string,
  /** When set, column visibility / order / widths persist to localStorage under
   *  this key and restore on the next visit. Omit for in-memory-only. */
  persistKey?: string,
) {
  // Lazy-init from the saved prefs (once) so a returning user sees their layout.
  const saved = useMemo(() => loadGridPrefs(persistKey), [persistKey]);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(saved.hidden ?? []));
  const [order, setOrder] = useState<string[] | null>(() => saved.order ?? null);
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => saved.widths ?? {});
  const [chooserAt, setChooserAt] = useState<{ x: number; y: number } | null>(null);
  const [dragField, setDragField] = useState<string | null>(null);
  const [dragOverField, setDragOverField] = useState<string | null>(null);
  const resizingRef = useRef(false);

  // Persist the column layout whenever it changes (best-effort; keyed writer).
  useEffect(() => {
    saveGridPrefs(persistKey, { hidden: [...hidden], order: order ?? undefined, widths: colWidths });
  }, [persistKey, hidden, order, colWidths]);

  const orderedColumns = useMemo(() => {
    if (!order) return columns;
    const idx = new Map(order.map((f, i) => [f, i] as const));
    return [...columns].sort((a, b) => (idx.get(keyOf(a)) ?? 1e9) - (idx.get(keyOf(b)) ?? 1e9));
    // keyOf is a stable caller function; intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, order]);
  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !hidden.has(keyOf(c))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedColumns, hidden],
  );

  // A grid always shows ≥1 column — the guard is computed from `prev` in the
  // setter so it never trips on a stale closure.
  const visibleCountAfter = (prev: Set<string>) =>
    orderedColumns.filter((c) => !prev.has(keyOf(c))).length;
  const hideColumn = (field: string) =>
    setHidden((prev) => {
      if (visibleCountAfter(prev) <= 1) return prev;
      const next = new Set(prev); next.add(field); return next;
    });
  const showAllColumns = () => setHidden(new Set());
  const toggleColumn = (field: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else if (visibleCountAfter(prev) > 1) next.add(field);
      return next;
    });
  const reorderColumns = (from: string, to: string) => {
    if (!from || from === to) return;
    setOrder(() => {
      const cur = orderedColumns.map(keyOf);
      const fi = cur.indexOf(from);
      if (fi < 0) return cur;
      cur.splice(fi, 1);
      const ti = cur.indexOf(to);
      cur.splice(ti < 0 ? cur.length : ti, 0, from);   // drop BEFORE the target
      return cur;
    });
  };

  const startResize = (field: string, e: ReactMouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = true;
    const th = (e.currentTarget as HTMLElement).closest('th') as HTMLElement | null;
    if (!th) return;
    const startX = e.clientX;
    const startW = th.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) =>
      setColWidths((prev) => ({ ...prev, [field]: Math.max(60, startW + (ev.clientX - startX)) }));
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setTimeout(() => { resizingRef.current = false; }, 0);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  /** Spread onto a header <th> to make it a drag-reorder source + drop target. */
  const dragProps = (field: string) => ({
    draggable: true,
    onDragStart: (e: ReactDragEvent) => {
      if (resizingRef.current) { e.preventDefault(); return; }
      setDragField(field);
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', field); } catch { /* older browsers */ }
    },
    onDragOver: (e: ReactDragEvent) => {
      if (dragField && dragField !== field) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverField !== field) setDragOverField(field);
      }
    },
    onDrop: (e: ReactDragEvent) => {
      e.preventDefault();
      if (dragField) reorderColumns(dragField, field);
      setDragField(null); setDragOverField(null);
    },
    onDragEnd: () => { setDragField(null); setDragOverField(null); },
  });
  /** Extra class names for the drag feedback (dim source · accent drop edge). */
  const dragClass = (field: string): string =>
    `${dragField === field ? ' adg-th-dragging' : ''}` +
    `${dragOverField === field && dragField && dragField !== field ? ' adg-th-dropbefore' : ''}`;

  const columnMenu: HeaderMenuColumns = {
    hideColumn,
    showAllColumns,
    hasHidden: hidden.size > 0,
    canHide: () => visibleColumns.length > 1,
    openChooser: (x, y) => setChooserAt({ x, y }),
  };

  const chooser = chooserAt
    ? createPortal(
        <ColumnChooser
          columns={orderedColumns.map((c) => ({ field: keyOf(c), label: labelOf(c) }))}
          hidden={hidden}
          position={chooserAt}
          onToggle={toggleColumn}
          onShowAll={showAllColumns}
          onClose={() => setChooserAt(null)}
        />,
        document.body,
      )
    : null;

  return {
    /** Columns to render (reordered + non-hidden), in display order. */
    visibleColumns,
    /** All columns in the user's order (incl. hidden) — for the chooser. */
    orderedColumns,
    hidden,
    /** User-dragged width for a column, or undefined (use the column's own). */
    colWidth: (field: string): number | undefined => colWidths[field],
    startResize,
    dragProps,
    dragClass,
    /** Pass to `useHeaderMenu(sorter, columnMenu)` to add Hide/Show-all/Chooser. */
    columnMenu,
    /** Render once in the grid tree — the Column Chooser popup (portals to body). */
    chooser,
  };
}
